/**
 * Cost Generation from Revenue Types
 * Types for generating BKP cost structures from revenue building programs
 */

/**
 * BFS Grossregionen (7 statistical regions) used for regional cost calibration.
 * Multipliers are applied to BKP 2 construction base rates.
 */
export type BfsRegion =
  | 'lemanique'
  | 'zurich'
  | 'ticino'
  | 'nordwestschweiz'
  | 'mittelland'
  | 'ostschweiz'
  | 'zentralschweiz'

export interface BfsRegionOption {
  value: BfsRegion
  label: { en: string; de: string; fr: string; it: string }
  cantons: string[]
  multiplier: number
}

export const BFS_REGION_OPTIONS: BfsRegionOption[] = [
  {
    value: 'lemanique',
    label: {
      en: 'Lake Geneva region',
      de: 'Genferseeregion',
      fr: 'Région lémanique',
      it: 'Regione del Lemano',
    },
    cantons: ['VD', 'GE', 'VS'],
    multiplier: 1.15,
  },
  {
    value: 'zurich',
    label: { en: 'Zurich', de: 'Zürich', fr: 'Zurich', it: 'Zurigo' },
    cantons: ['ZH'],
    multiplier: 1.1,
  },
  {
    value: 'ticino',
    label: { en: 'Ticino', de: 'Tessin', fr: 'Tessin', it: 'Ticino' },
    cantons: ['TI'],
    multiplier: 1.1,
  },
  {
    value: 'nordwestschweiz',
    label: {
      en: 'Northwest Switzerland',
      de: 'Nordwestschweiz',
      fr: 'Suisse du Nord-Ouest',
      it: 'Svizzera nordoccidentale',
    },
    cantons: ['BS', 'BL', 'AG'],
    multiplier: 1.0,
  },
  {
    value: 'mittelland',
    label: {
      en: 'Espace Mittelland',
      de: 'Espace Mittelland',
      fr: 'Espace Mittelland',
      it: 'Altipiano',
    },
    cantons: ['BE', 'FR', 'SO', 'NE', 'JU'],
    multiplier: 0.97,
  },
  {
    value: 'ostschweiz',
    label: {
      en: 'Eastern Switzerland',
      de: 'Ostschweiz',
      fr: 'Suisse orientale',
      it: 'Svizzera orientale',
    },
    cantons: ['SG', 'TG', 'AR', 'AI', 'GR', 'GL', 'SH'],
    multiplier: 0.95,
  },
  {
    value: 'zentralschweiz',
    label: {
      en: 'Central Switzerland',
      de: 'Zentralschweiz',
      fr: 'Suisse centrale',
      it: 'Svizzera centrale',
    },
    cantons: ['LU', 'ZG', 'SZ', 'NW', 'OW', 'UR'],
    multiplier: 0.95,
  },
]

export const REGIONAL_MULTIPLIERS: Record<BfsRegion, number> =
  Object.fromEntries(
    BFS_REGION_OPTIONS.map((o) => [o.value, o.multiplier])
  ) as Record<BfsRegion, number>

/**
 * Normalized canton aliases → 2-letter code, used to auto-detect the BFS region
 * from `land.canton_name` which can come in several formats (full name in FR/DE/IT/EN or code).
 */
const CANTON_ALIASES: Record<string, string> = {
  // Codes (already canonical)
  vd: 'VD',
  ge: 'GE',
  vs: 'VS',
  zh: 'ZH',
  ti: 'TI',
  bs: 'BS',
  bl: 'BL',
  ag: 'AG',
  be: 'BE',
  fr: 'FR',
  so: 'SO',
  ne: 'NE',
  ju: 'JU',
  sg: 'SG',
  tg: 'TG',
  ar: 'AR',
  ai: 'AI',
  gr: 'GR',
  gl: 'GL',
  sh: 'SH',
  lu: 'LU',
  zg: 'ZG',
  sz: 'SZ',
  nw: 'NW',
  ow: 'OW',
  ur: 'UR',
  // Names (FR / DE / IT / EN)
  vaud: 'VD',
  waadt: 'VD',
  geneva: 'GE',
  genève: 'GE',
  genf: 'GE',
  ginevra: 'GE',
  valais: 'VS',
  wallis: 'VS',
  vallese: 'VS',
  zurich: 'ZH',
  zürich: 'ZH',
  zurigo: 'ZH',
  ticino: 'TI',
  tessin: 'TI',
  'basel-stadt': 'BS',
  bale: 'BS',
  'bâle-ville': 'BS',
  'basel-landschaft': 'BL',
  'bâle-campagne': 'BL',
  aargau: 'AG',
  argovie: 'AG',
  argovia: 'AG',
  bern: 'BE',
  berne: 'BE',
  berna: 'BE',
  fribourg: 'FR',
  freiburg: 'FR',
  friburgo: 'FR',
  solothurn: 'SO',
  soleure: 'SO',
  soletta: 'SO',
  neuchâtel: 'NE',
  neuchatel: 'NE',
  neuenburg: 'NE',
  jura: 'JU',
  'st. gallen': 'SG',
  'sankt gallen': 'SG',
  'st-gall': 'SG',
  'san gallo': 'SG',
  thurgau: 'TG',
  thurgovie: 'TG',
  turgovia: 'TG',
  'appenzell ausserrhoden': 'AR',
  'appenzell-rhodes-extérieures': 'AR',
  'appenzell innerrhoden': 'AI',
  'appenzell-rhodes-intérieures': 'AI',
  graubünden: 'GR',
  grisons: 'GR',
  grigioni: 'GR',
  grisun: 'GR',
  glarus: 'GL',
  glaris: 'GL',
  glarona: 'GL',
  schaffhausen: 'SH',
  schaffhouse: 'SH',
  sciaffusa: 'SH',
  luzern: 'LU',
  lucerne: 'LU',
  lucerna: 'LU',
  zug: 'ZG',
  zoug: 'ZG',
  zugo: 'ZG',
  schwyz: 'SZ',
  svitto: 'SZ',
  nidwalden: 'NW',
  nidwald: 'NW',
  nidvaldo: 'NW',
  obwalden: 'OW',
  obwald: 'OW',
  obvaldo: 'OW',
  uri: 'UR',
}

/**
 * Auto-detect the BFS Grossregion from a canton name/code.
 * Returns null if the canton cannot be resolved.
 */
export const detectBfsRegionFromCanton = (
  canton: string | null | undefined
): BfsRegion | null => {
  if (!canton) return null
  const code = CANTON_ALIASES[canton.trim().toLowerCase()]
  if (!code) return null
  const region = BFS_REGION_OPTIONS.find((r) => r.cantons.includes(code))
  return region?.value ?? null
}

/**
 * Site complexity levels — drive BKP 1 (preparatory works) as % of BKP 2.
 */
export type SiteComplexity = 'greenfield' | 'standard' | 'difficult'

export interface SiteComplexityOption {
  value: SiteComplexity
  label: { en: string; de: string; fr: string; it: string }
  description: { en: string; de: string; fr: string; it: string }
  preparationPercent: number
}

export const SITE_COMPLEXITY_OPTIONS: SiteComplexityOption[] = [
  {
    value: 'greenfield',
    label: {
      en: 'Greenfield',
      de: 'Grüne Wiese',
      fr: 'Terrain vierge',
      it: 'Area vergine',
    },
    description: {
      en: 'Empty parcel, no demolition, level ground, no soil issues.',
      de: 'Leere Parzelle, kein Abbruch, ebenes Gelände, keine Bodenprobleme.',
      fr: 'Parcelle vide, pas de démolition, terrain plat, sol sans problème.',
      it: 'Parcella vuota, nessuna demolizione, terreno piano, suolo senza problemi.',
    },
    preparationPercent: 2,
  },
  {
    value: 'standard',
    label: {
      en: 'Standard urban',
      de: 'Standard städtisch',
      fr: 'Urbain standard',
      it: 'Urbano standard',
    },
    description: {
      en: 'Typical urban infill, basic site preparation, no major obstacles.',
      de: 'Typische städtische Nachverdichtung, einfache Vorbereitung, keine grösseren Hindernisse.',
      fr: "Densification urbaine typique, préparation de base, pas d'obstacles majeurs.",
      it: 'Tipica densificazione urbana, preparazione di base, nessun ostacolo maggiore.',
    },
    preparationPercent: 5,
  },
  {
    value: 'difficult',
    label: {
      en: 'Difficult site',
      de: 'Schwieriger Standort',
      fr: 'Site difficile',
      it: 'Sito difficile',
    },
    description: {
      en: 'Existing building to demolish, contaminated soil, rock excavation, or steep slope.',
      de: 'Bestehender Abbruch, belastetes Erdreich, Felsaushub oder Steilhang.',
      fr: 'Bâtiment à démolir, sol contaminé, terrassement rocheux ou pente raide.',
      it: 'Edificio da demolire, suolo contaminato, scavo in roccia o pendio ripido.',
    },
    preparationPercent: 15,
  },
]

/**
 * Attic finish type — only relevant when the revenue program contains DG floors.
 * Drives the multiplier applied to the attic area in BKP 2 residential.
 */
export type AtticType = 'habitable' | 'cold'

export interface AtticTypeOption {
  value: AtticType
  label: { en: string; de: string; fr: string; it: string }
  description: { en: string; de: string; fr: string; it: string }
  multiplier: number
}

export const ATTIC_TYPE_OPTIONS: AtticTypeOption[] = [
  {
    value: 'habitable',
    label: {
      en: 'Habitable attic',
      de: 'Ausgebautes Dachgeschoss',
      fr: 'Combles habitables',
      it: 'Sottotetto abitabile',
    },
    description: {
      en: 'Living space with dormers, insulation, full finish. Roof structure adds ~15% over a standard floor.',
      de: 'Wohnraum mit Gauben, Dämmung, Vollausbau. Dachkonstruktion ~15% über einem Standardgeschoss.',
      fr: 'Espace habitable avec lucarnes, isolation, finition complète. Structure de toit ~+15% sur un étage standard.',
      it: 'Spazio abitativo con abbaini, isolamento, finitura completa. Struttura del tetto ~+15% rispetto a un piano standard.',
    },
    multiplier: 1.15,
  },
  {
    value: 'cold',
    label: {
      en: 'Cold attic / storage',
      de: 'Kaltes Dachgeschoss / Estrich',
      fr: 'Combles froids / stockage',
      it: 'Sottotetto freddo / ripostiglio',
    },
    description: {
      en: 'Unheated, unfinished, used as attic storage only. Half the cost of a finished floor.',
      de: 'Unbeheizt, unausgebaut, nur als Estrich genutzt. Etwa die Hälfte der Kosten eines ausgebauten Geschosses.',
      fr: "Non chauffé, non aménagé, utilisé comme grenier. Environ la moitié du coût d'un étage fini.",
      it: 'Non riscaldato, non rifinito, utilizzato solo come ripostiglio. Circa la metà del costo di un piano finito.',
    },
    multiplier: 0.5,
  },
]

/**
 * BKP 2 base cost rates in CHF per m³ of SIA 416 Gebäudevolumen (GV).
 *
 * Sourced from public Swiss benchmarks (aktiva.swiss Immobilien-Benchmarks
 * 2023, Wüest Partner / Lignum 2023 office study, hafag.ch turnkey hall
 * pricelist) with a uniform +6% BFS Baupreisindex drift applied to translate
 * 2023 source numbers to 2026.
 *
 * Use these as the "durchschnittlich" quality × 1.00, "Hülle isoliert" energy
 * × 1.00, national-average baseline. The calibration modal layers regional,
 * quality, energy, attic-type and site-complexity multipliers on top.
 *
 * National average — DO NOT bake regional uplift into these constants.
 *
 * Sources documented in the cost mapper service file header.
 */
export const BASE_COST_RATES_M3: Record<string, number> = {
  residential: 1000, // above the aktiva MFH 680–900 CHF/m³ band; quality bias toward Eigentum-plus
  commercial_office: 1000, // aktiva Verwaltungsbau 950 CHF/m³ + 6% BFS drift
  commercial_retail: 900, // anchored to office parity at BKP 2 shell (no public Swiss CHF/m³ figure for retail shell)
  industrial: 350, // hafag.ch heated workshop ~1600 CHF/m² ÷ 5.0 m default storey height
  storage_cellar: 450, // hafag.ch unheated norm storage ~1100 CHF/m² ÷ ~2.8 m
  parking_indoor: 600, // 40k CHF/space ÷ ~25 m² per stall ÷ 2.6 m basement height
}

/**
 * Default storey heights in meters, used to convert user-entered floor areas
 * into volumes for BKP 2 pricing. Keyed by use type, with residential split
 * by floor type because Swiss MFH typically have a slightly taller ground
 * floor and shorter basement / attic.
 *
 * These are mid-band SIA-typical values. They do not include slab thickness;
 * the published CHF/m³ rates already factor in the SIA 416 GV definition
 * (which includes slab and roof package).
 */
export interface ResidentialStoreyHeights {
  basement: number
  ground_floor: number
  upper_floor: number
  attic: number
}

export interface DefaultStoreyHeights {
  residential: ResidentialStoreyHeights
  commercial_office: number
  commercial_retail: number
  industrial: number
  storage_cellar: number
  parking_indoor: number
}

/**
 * Flat net-to-gross floor area factor (SIA 416 NF → GF/SP). Single source for
 * the floor area we send to keeValue (`totalFloorArea416`) and for the
 * `revenue.floor_area_416` quantity binding, so a keeValue-generated row's
 * quantity always equals the area keeValue priced on. Not the per-index
 * zoning factors (`NF_TO_GF_FACTOR_BY_INDEX`), which serve utilisation math.
 */
export const NF_TO_GF_FACTOR = 1.3

export const DEFAULT_STOREY_HEIGHTS: DefaultStoreyHeights = {
  residential: {
    basement: 2.5,
    ground_floor: 3.0,
    upper_floor: 2.7,
    attic: 2.5,
  },
  commercial_office: 3.3,
  commercial_retail: 4.0,
  industrial: 5.0, // heated workshop baseline; logistics halls (8-12 m) are mis-priced
  storage_cellar: 2.8,
  parking_indoor: 2.6,
}

/**
 * Options passed to generateCostsFromRevenue to calibrate the output.
 * All fields are optional — each has a sensible default.
 */
export interface CostGenerationOptions {
  region?: BfsRegion
  site_complexity?: SiteComplexity
  attic_type?: AtticType
  quality_override?: string // StandardLevel
  energy_override?: string // EnergyStandardLevel
}

/**
 * Area breakdown by floor type (UG, EG, OG, DG)
 */
export interface FloorTypeAreaBreakdown {
  basement_area_m2: number // UG floors
  ground_floor_area_m2: number // EG floors
  upper_floor_area_m2: number // OG floors
  attic_area_m2: number // DG floors
}

/**
 * Volume breakdown by floor type (UG, EG, OG, DG) — each is the m² figure
 * times the residential per-floor-type default storey height.
 */
export interface FloorTypeVolumeBreakdown {
  basement_volume_m3: number
  ground_floor_volume_m3: number
  upper_floor_volume_m3: number
  attic_volume_m3: number
}

/**
 * Summary of revenue data used as input for cost generation.
 *
 * Area fields (CHF/m² heritage) are kept for backward compatibility with
 * non-cost consumers (Excel export, sync banner). Volume fields drive the
 * new BKP 2 pricing model.
 */
export interface RevenueSourceSummary {
  residential_area_m2: number
  commercial_office_area_m2: number
  commercial_retail_area_m2: number
  industrial_area_m2: number
  storage_cellar_area_m2: number
  total_construction_area: number
  // Per-floor-type area breakdown (all categories combined)
  floor_type_areas: FloorTypeAreaBreakdown

  // Volume fields used by the volume-based BKP 2 pricing model.
  // Computed as area × DEFAULT_STOREY_HEIGHTS[use_type] (or per-floor-type
  // height for residential). Industrial volume is taken directly from the
  // user-entered RevenueUnit.volume_m3.
  residential_volume_m3?: number
  commercial_office_volume_m3?: number
  commercial_retail_volume_m3?: number
  industrial_volume_m3?: number
  storage_cellar_volume_m3?: number
  floor_type_volumes?: FloorTypeVolumeBreakdown

  parking_indoor_count: number
  parking_outdoor_count: number
  floor_count: number
  standard: string
  energy_standard: string
  standard_multiplier: number
  energy_multiplier: number
}

/**
 * Result of generating costs from revenue data
 */
export interface GeneratedCostResult {
  items_created: number
  total_estimated_cost: number
  breakdown: {
    land: number
    preparation: number
    construction: number
    equipment: number
    surroundings: number
    ancillary: number
    reserve: number
  }
  source_summary: RevenueSourceSummary
}

/**
 * A single change detected between revenue snapshots
 */
export interface RevenueSyncChange {
  field: string
  previous_value: number | string
  current_value: number | string
  change_percent?: number
}

/**
 * Status of revenue-cost synchronization
 */
export interface RevenueSyncStatus {
  has_significant_changes: boolean
  last_generated_at: string | null
  changes: RevenueSyncChange[]
}
